import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Select, SelectItem, Label, Input, Textarea, DateInput } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { PlusIcon, TrashIcon, ChevronDownIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * DeviationActionsIntegration - Component for managing deviation actions within the deviation form
 * Similar to ComplaintActionsIntegration but for DeviationActions
 */
const DeviationActionsIntegration = ({ 
  deviationId,
  formData,
  setFormData,
  config,
  isEditMode = false,
  selectedRecord = null // For view mode
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localActions, setLocalActions] = useState([]);

  // Determine if we're in view mode
  const isViewMode = selectedRecord && !isEditMode;
  const recordId = selectedRecord?.id || deviationId || formData?.id;

  // Fetch lookup data
  const { data: actionTypes = [], isLoading: actionTypesLoading } = useFlexibleList(
    config?.api?.lookupEndpoints?.deviationActionTypes ? config.api.lookupEndpoints.deviationActionTypes : null
  );

  // Fetch existing actions if deviationId exists (for both edit and view mode)
  const { data: existingActions = [] } = useFlexibleList(
    recordId ? `/deviation-action/by-deviation/${recordId}` : null
  );

  // Track if we've initialized to prevent infinite loops
  const isInitializedRef = React.useRef(false);
  const prevDeviationIdRef = React.useRef(recordId);
  
  // Use ref to store latest actions for immediate access
  const localActionsRef = React.useRef([]);

  // Helper function to get action type record_order
  const getActionTypeOrder = useCallback((action) => {
    // First try to get from populated actionType relation
    if (action.actionType?.record_order !== null && action.actionType?.record_order !== undefined) {
      return action.actionType.record_order;
    }
    // Otherwise, look it up from actionTypes data
    if (action.actionTypeId && actionTypes.length > 0) {
      const actionType = actionTypes.find(type => type.id === action.actionTypeId);
      if (actionType?.record_order !== null && actionType?.record_order !== undefined) {
        return actionType.record_order;
      }
    }
    // Fallback to ID if no record_order
    return action.id || 0;
  }, [actionTypes]);

  // Initialize actions only once when component mounts or deviationId changes
  useEffect(() => {
    // Reset initialization flag when deviationId changes
    if (prevDeviationIdRef.current !== recordId) {
      isInitializedRef.current = false;
      prevDeviationIdRef.current = recordId;
    }

    // Only initialize if we haven't already done so
    if (isInitializedRef.current) return;

    // Priority: existingActions (from API) > selectedRecord.actions (view mode) > formData.deviationActions > empty array
    // IMPORTANT: Always preserve implementation status fields from formData if they exist
    let actionsToSet = [];
    if (existingActions && Array.isArray(existingActions) && existingActions.length > 0) {
      // Load existing actions from API if available
      // Merge with formData to preserve any implementation status fields that were set
      const formDataActions = formData?.deviationActions || [];
      actionsToSet = existingActions.map(apiAction => {
        // Try to find matching action in formData by ID
        const formDataAction = formDataActions.find(fa => 
          fa.id && apiAction.id && fa.id.toString() === apiAction.id.toString()
        );
        
        // If found, preserve implementation status fields from formData
        if (formDataAction) {
          return {
            ...apiAction,
            implementationStatus: formDataAction.implementationStatus !== undefined 
              ? formDataAction.implementationStatus 
              : apiAction.implementationStatus,
            newExtension: formDataAction.newExtension !== undefined 
              ? formDataAction.newExtension 
              : apiAction.newExtension,
            qaResponsible: formDataAction.qaResponsible !== undefined 
              ? formDataAction.qaResponsible 
              : apiAction.qaResponsible,
            newDueDate: formDataAction.newDueDate !== undefined 
              ? formDataAction.newDueDate 
              : apiAction.newDueDate,
            newImplementationStatus: formDataAction.newImplementationStatus !== undefined 
              ? formDataAction.newImplementationStatus 
              : apiAction.newImplementationStatus,
            qaDate: formDataAction.qaDate !== undefined 
              ? formDataAction.qaDate 
              : apiAction.qaDate
          };
        }
        
        return apiAction;
      });
    } else if (selectedRecord && selectedRecord.deviationActions && Array.isArray(selectedRecord.deviationActions) && selectedRecord.deviationActions.length > 0) {
      // For view mode, use actions from selectedRecord
      actionsToSet = selectedRecord.deviationActions;
    } else {
      // For new records OR edit mode without existing actions, check formData first, then initialize empty array
      if (formData && formData.deviationActions && Array.isArray(formData.deviationActions) && formData.deviationActions.length > 0) {
        actionsToSet = formData.deviationActions;
      } else {
        actionsToSet = [];
      }
    }
    
    // Sort actions by actionType.record_order to ensure consistent order between CAPA and Implementation sections
    actionsToSet = [...actionsToSet].sort((a, b) => {
      const orderA = getActionTypeOrder(a);
      const orderB = getActionTypeOrder(b);
      
      // Sort by record_order, then by ID as fallback
      if (orderA !== orderB) {
        return (orderA || 999999) - (orderB || 999999);
      }
      return (a.id || 0) - (b.id || 0);
    });
    
    setLocalActions(actionsToSet);
    isInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, isEditMode, isViewMode, existingActions, selectedRecord, formData?.deviationActions, getActionTypeOrder]);

  // Re-sort actions when actionTypes are loaded (to use record_order)
  useEffect(() => {
    if (actionTypes.length > 0 && localActions.length > 0 && isInitializedRef.current) {
      const sortedActions = [...localActions].sort((a, b) => {
        const orderA = getActionTypeOrder(a);
        const orderB = getActionTypeOrder(b);
        
        // Sort by record_order, then by ID as fallback
        if (orderA !== orderB) {
          return (orderA || 999999) - (orderB || 999999);
        }
        return (a.id || 0) - (b.id || 0);
      });
      
      // Only update if order changed
      const currentOrder = JSON.stringify(localActions.map(a => a.id));
      const newOrder = JSON.stringify(sortedActions.map(a => a.id));
      if (currentOrder !== newOrder) {
        setLocalActions(sortedActions);
      }
    }
  }, [actionTypes, getActionTypeOrder]);

  // Update ref whenever localActions changes
  useEffect(() => {
    localActionsRef.current = localActions;
  }, [localActions]);

  // Sync localActions to formData (but only if we've initialized and not in view mode)
  useEffect(() => {
    if (!isInitializedRef.current || isViewMode) return;

    const syncToFormData = () => {
      setFormData(prev => {
        const currentActions = prev.deviationActions || [];
        
        // Compare only CAPA-related fields (not implementation status fields) to determine if sync is needed
        const getCAPAFields = (action) => ({
          id: action.id,
          actionTypeId: action.actionTypeId || action.actionType?.id,
          description: action.description,
          responsible: action.responsible,
          dueDate: action.dueDate,
          closeDate: action.closeDate,
          startedAt: action.startedAt
        });
        
        const currentCAPAFields = currentActions.map(getCAPAFields);
        const localCAPAFields = localActions.map(getCAPAFields);
        const currentCAPAStr = JSON.stringify(currentCAPAFields);
        const localCAPAStr = JSON.stringify(localCAPAFields);
        
        // Only skip sync if CAPA fields are the same (ignore implementation status fields in comparison)
        if (currentCAPAStr === localCAPAStr && currentActions.length === localActions.length) {
          return prev;
        }
        
        // Merge localActions with existing formData actions to preserve implementation status fields
        // Match actions by ID or by actionTypeId + description to preserve implementation status
        const mergedActions = localActions.map(localAction => {
          // Try to find matching action in current formData by ID first
          let existingAction = currentActions.find(ca => 
            ca.id && localAction.id && ca.id.toString() === localAction.id.toString()
          );
          
          // If no match by ID, try to match by actionTypeId and description
          if (!existingAction) {
            const localActionTypeId = localAction.actionTypeId || localAction.actionType?.id;
            existingAction = currentActions.find(ca => {
              const caActionTypeId = ca.actionTypeId || ca.actionType?.id;
              return caActionTypeId === localActionTypeId && 
                     ca.description?.trim() === localAction.description?.trim();
            });
          }
          
          // If we found an existing action, preserve implementation status fields
          if (existingAction) {
            return {
              ...localAction,
              // Preserve implementation status fields from existing action
              implementationStatus: existingAction.implementationStatus !== undefined 
                ? existingAction.implementationStatus 
                : localAction.implementationStatus,
              newExtension: existingAction.newExtension !== undefined 
                ? existingAction.newExtension 
                : localAction.newExtension,
              qaResponsible: existingAction.qaResponsible !== undefined 
                ? existingAction.qaResponsible 
                : localAction.qaResponsible,
              newDueDate: existingAction.newDueDate !== undefined 
                ? existingAction.newDueDate 
                : localAction.newDueDate,
              newImplementationStatus: existingAction.newImplementationStatus !== undefined 
                ? existingAction.newImplementationStatus 
                : localAction.newImplementationStatus,
              qaDate: existingAction.qaDate !== undefined 
                ? existingAction.qaDate 
                : localAction.qaDate
            };
          }
          
          // No existing action found, use localAction as-is
          return localAction;
        });
        
        console.log('🔄 Syncing actions to formData, preserving implementation status fields:', {
          localActionsCount: localActions.length,
          mergedActionsCount: mergedActions.length,
          sampleMerged: mergedActions[0] ? {
            id: mergedActions[0].id,
            implementationStatus: mergedActions[0].implementationStatus,
            newImplementationStatus: mergedActions[0].newImplementationStatus
          } : null
        });
        
        return {
          ...prev,
          deviationActions: mergedActions
        };
      });
    };
    
    syncToFormData();
  }, [localActions, setFormData, isViewMode]);
  
  // Force sync before form submission - expose sync function via window for debugging
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__syncDeviationActions = () => {
        const latestActions = localActionsRef.current.length > 0 
          ? localActionsRef.current 
          : localActions;
        
        // CRITICAL: Preserve implementation status fields when syncing
        setFormData(prev => {
          const currentFormActions = prev.deviationActions || [];
          
          // Merge latestActions with currentFormActions to preserve implementation status fields
          const mergedActions = latestActions.map(localAction => {
            // Try to find matching action in formData by ID first
            let existingAction = currentFormActions.find(ca => 
              ca.id && localAction.id && ca.id.toString() === localAction.id.toString()
            );
            
            // If no match by ID, try to match by actionTypeId and description
            if (!existingAction) {
              const localActionTypeId = localAction.actionTypeId || localAction.actionType?.id;
              existingAction = currentFormActions.find(ca => {
                const caActionTypeId = ca.actionTypeId || ca.actionType?.id;
                return caActionTypeId === localActionTypeId && 
                       ca.description?.trim() === localAction.description?.trim();
              });
            }
            
            // If we found an existing action, preserve implementation status fields
            if (existingAction) {
              return {
                ...localAction,
                // Preserve implementation status fields from existing formData action
                implementationStatus: existingAction.implementationStatus !== undefined 
                  ? existingAction.implementationStatus 
                  : localAction.implementationStatus,
                newExtension: existingAction.newExtension !== undefined 
                  ? existingAction.newExtension 
                  : localAction.newExtension,
                qaResponsible: existingAction.qaResponsible !== undefined 
                  ? existingAction.qaResponsible 
                  : localAction.qaResponsible,
                newDueDate: existingAction.newDueDate !== undefined 
                  ? existingAction.newDueDate 
                  : localAction.newDueDate,
                newImplementationStatus: existingAction.newImplementationStatus !== undefined 
                  ? existingAction.newImplementationStatus 
                  : localAction.newImplementationStatus,
                qaDate: existingAction.qaDate !== undefined 
                  ? existingAction.qaDate 
                  : localAction.qaDate
              };
            }
            
            // No existing action found, use localAction as-is
            return localAction;
          });
          
          console.log('🔄 __syncDeviationActions: Preserving implementation status fields', {
            latestActionsCount: latestActions.length,
            mergedActionsCount: mergedActions.length,
            sampleMerged: mergedActions[0] ? {
              id: mergedActions[0].id,
              implementationStatus: mergedActions[0].implementationStatus,
              newImplementationStatus: mergedActions[0].newImplementationStatus
            } : null
          });
          
          const updated = {
            ...prev,
            deviationActions: mergedActions
          };
          localActionsRef.current = latestActions;
          return updated;
        });
      };
      
      // Also expose getter for debugging and direct data access
      // CRITICAL: Return formData actions (which have implementation status) instead of localActions
      window.__getDeviationActions = () => {
        // Prioritize formData actions as they contain implementation status fields
        const formDataActions = formData?.deviationActions;
        if (formDataActions && Array.isArray(formDataActions) && formDataActions.length > 0) {
          console.log('🔄 __getDeviationActions: Returning formData actions (with implementation status)', {
            count: formDataActions.length,
            sample: formDataActions[0] ? {
              id: formDataActions[0].id,
              implementationStatus: formDataActions[0].implementationStatus,
              newImplementationStatus: formDataActions[0].newImplementationStatus
            } : null
          });
          return {
            localActions: formDataActions,
            localActionsState: formDataActions,
            isInitialized: isInitializedRef.current,
            formData: formDataActions
          };
        }
        
        // Fallback to localActions if formData doesn't have actions
        const latestActions = localActionsRef.current.length > 0 
          ? localActionsRef.current 
          : localActions;
        console.log('🔄 __getDeviationActions: Falling back to localActions (no formData actions)', {
          count: latestActions.length
        });
        return {
          localActions: latestActions,
          localActionsState: localActions,
          isInitialized: isInitializedRef.current,
          formData: formData?.deviationActions
        };
      };
    }
  }, [setFormData, formData, localActions]);

  const addAction = () => {
    const newAction = {
      id: `temp-${Date.now()}`,
      actionTypeId: null,
      description: '',
      responsible: '',
      dueDate: null,
      closeDate: null,
      implementationStatus: null,
      newExtension: null,
      qaResponsible: '',
      newDueDate: null,
      newImplementationStatus: null,
      qaDate: null,
      isNew: true
    };
    setLocalActions(prev => {
      const updated = [...prev, newAction];
      localActionsRef.current = updated;
      return updated;
    });
    setIsExpanded(true);
  };

  const removeAction = (index) => {
    setLocalActions(prev => {
      const newActions = prev.filter((_, i) => i !== index);
      localActionsRef.current = newActions;
      
      // Immediately sync to formData so Implementation section also reflects the deletion
      // Always set to an array (even if empty) to ensure deletions are properly tracked
      if (setFormData && !isViewMode) {
        setFormData(prevFormData => ({
          ...prevFormData,
          deviationActions: Array.isArray(newActions) ? newActions : []
        }));
        console.log('🔄 Removed action, synced to formData. New actions count:', newActions.length);
      }
      
      return newActions;
    });
  };

  const updateAction = (index, field, value) => {
    setLocalActions(prev => {
      const newActions = [...prev];
      // Preserve the existing action data, only update the specific field
      newActions[index] = {
        ...newActions[index],
        [field]: value
      };
      localActionsRef.current = newActions;
      
      // Also immediately sync to formData to preserve implementation status fields
      if (setFormData && !isViewMode) {
        setFormData(prevFormData => {
          const currentActions = prevFormData.deviationActions || [];
          const updatedActions = [...currentActions];
          
          // Find the matching action in formData and update it
          const actionToUpdate = newActions[index];
          const formDataIndex = updatedActions.findIndex((fa, idx) => {
            // Match by ID if available
            if (fa.id && actionToUpdate.id && fa.id.toString() === actionToUpdate.id.toString()) {
              return true;
            }
            // Match by index if IDs don't match
            return idx === index;
          });
          
          if (formDataIndex >= 0) {
            // Update the action in formData, preserving all fields including implementation status
            updatedActions[formDataIndex] = {
              ...updatedActions[formDataIndex],
              ...actionToUpdate
            };
          } else if (index < updatedActions.length) {
            // Fallback: update by index
            updatedActions[index] = {
              ...updatedActions[index],
              ...actionToUpdate
            };
          }
          
          return {
            ...prevFormData,
            deviationActions: updatedActions
          };
        });
      }
      
      return newActions;
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className="font-semibold">Corrective / Preventive Actions</span>
          {localActions.length > 0 && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              theme.colors.primary,
              theme.text.inverse
            )}>
              {localActions.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t", theme.border.light)}>
          {localActions.length === 0 ? (
            <div className={cn("text-center py-8", theme.text.secondary)}>
              <p className="mb-4">No actions added yet.</p>
              {!isViewMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAction}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add First Action
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {localActions.map((action, index) => (
                <div
                  key={action.id || index}
                  className={cn(
                    "p-4 rounded-lg border space-y-4",
                    theme.border.subtle,
                    theme.colors.backgroundAlt
                  )}
                >
                  {/* Action Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-medium", theme.text.secondary)}>
                        Action #{index + 1}
                      </span>
                      {action.closeDate && (
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium",
                          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        )}>
                          Closed
                        </span>
                      )}
                    </div>
                    {!isViewMode && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeAction(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Action Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* Action Type */}
                    <div className="md:col-span-4">
                      <Label htmlFor={`actionType-${index}`}>
                        Action Type <span className="text-red-500">*</span>
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {action.actionType?.name || '—'}
                        </div>
                      ) : (
                        <Select
                          id={`actionType-${index}`}
                          value={action.actionTypeId?.toString() || ''}
                          onChange={(e) => updateAction(index, 'actionTypeId', e.target.value ? parseInt(e.target.value) : null)}
                          disabled={actionTypesLoading}
                        >
                          <SelectItem value="">Select action type</SelectItem>
                          {actionTypes?.map((type) => (
                            <SelectItem key={type.id} value={type.id.toString()}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                    </div>

                    {/* Responsible */}
                    <div className="md:col-span-4">
                      <Label htmlFor={`responsible-${index}`}>
                        Responsible
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {action.responsible || '—'}
                        </div>
                      ) : (
                        <Input
                          id={`responsible-${index}`}
                          type="text"
                          value={action.responsible || ''}
                          onChange={(e) => updateAction(index, 'responsible', e.target.value)}
                          placeholder="Enter responsible person"
                        />
                      )}
                    </div>

                    {/* Due Date */}
                    <div className="md:col-span-4">
                      <Label htmlFor={`dueDate-${index}`}>
                        Due Date
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {action.dueDate ? new Date(action.dueDate).toLocaleDateString() : '—'}
                        </div>
                      ) : (
                        <DateInput
                          id={`dueDate-${index}`}
                          value={formatDateForInput(action.dueDate)}
                          onChange={(e) => {
                            const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                            updateAction(index, 'dueDate', dateValue);
                          }}
                        />
                      )}
                    </div>

                    {/* Description - Full Width */}
                    <div className="md:col-span-12">
                      <Label htmlFor={`description-${index}`}>
                        Description <span className="text-red-500">*</span>
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded whitespace-pre-wrap", theme.colors.background, theme.text.primary)}>
                          {action.description || '—'}
                        </div>
                      ) : (
                        <Textarea
                          id={`description-${index}`}
                          value={action.description || ''}
                          onChange={(e) => updateAction(index, 'description', e.target.value)}
                          placeholder="Enter action description"
                          rows={3}
                        />
                      )}
                    </div>

                    {/* Close Date */}
                    <div className="md:col-span-4">
                      <Label htmlFor={`closeDate-${index}`}>
                        Close Date
                      </Label>
                      {isViewMode ? (
                        <div className={cn("text-sm p-2 rounded", theme.colors.background, theme.text.primary)}>
                          {action.closeDate ? new Date(action.closeDate).toLocaleDateString() : '—'}
                        </div>
                      ) : (
                        <DateInput
                          id={`closeDate-${index}`}
                          value={formatDateForInput(action.closeDate)}
                          onChange={(e) => {
                            const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                            updateAction(index, 'closeDate', dateValue);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Action Button - Only show in edit mode */}
              {!isViewMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAction}
                  className="w-full"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Another Action
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviationActionsIntegration;

